/**
 * Textarea Component
 */

import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { transformStyleProps, splitProps, stylePropsKeys } from '../system/style-props';
import { useTheme } from '../theme';

const StyledTextarea = styled('textarea', {
  shouldForwardProp: (prop) => {
    if (stylePropsKeys.includes(prop)) return false;
    if (['as', 'sx', '__css', 'variant', 'size', 'isDisabled', 
         'isInvalid', 'isReadOnly', 'isRequired'].includes(prop)) return false;
    return true;
  },
})(({ theme, ...props }) => {
  const styles = [];

  if (props.__css) {
    const cssResult = typeof props.__css === 'function'
      ? props.__css({ theme, colorMode: 'light', ...props })
      : props.__css;
    styles.push(transformStyleProps(cssResult, theme));
  }

  const [styleProps] = splitProps(props);
  styles.push(transformStyleProps(styleProps, theme));

  if (props.sx) {
    const sxResult = typeof props.sx === 'function'
      ? props.sx({ theme, ...props })
      : props.sx;
    styles.push(transformStyleProps(sxResult, theme));
  }

  return styles;
});

/**
 * Textarea Component
 * 
 * @example
 * <Textarea placeholder="Enter description" rows={4} />
 */
export const Textarea = forwardRef(({
  as,
  variant = 'outline',
  size = 'md',
  isDisabled = false,
  isInvalid = false,
  isReadOnly = false,
  isRequired = false,
  resize = 'vertical',
  ...props
}, ref) => {
  const theme = useTheme();
  const textareaStyles = theme?.components?.Textarea || {};
  
  const baseStyle = textareaStyles.baseStyle || {};
  const sizeStyle = textareaStyles.sizes?.[size] || {};
  const variantStyles = typeof textareaStyles.variants?.[variant] === 'function'
    ? textareaStyles.variants[variant]({ colorMode: 'light' })
    : textareaStyles.variants?.[variant] || {};

  const mergedStyles = {
    width: '100%',
    minWidth: 0,
    outline: 0,
    position: 'relative',
    appearance: 'none',
    transition: 'all 0.2s',
    minHeight: '80px',
    lineHeight: 'short',
    resize,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray.200',
    ...baseStyle,
    ...sizeStyle,
    ...variantStyles,
    ...(isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
    ...(isInvalid ? { borderColor: 'red.500' } : {}),
  };

  const cleanStyles = { ...mergedStyles };
  ['_hover', '_focus', '_disabled', '_invalid', '_placeholder', 'field'].forEach(key => {
    delete cleanStyles[key];
  });

  return (
    <StyledTextarea
      ref={ref}
      as={as}
      disabled={isDisabled}
      readOnly={isReadOnly}
      required={isRequired}
      aria-invalid={isInvalid}
      data-invalid={isInvalid || undefined}
      __css={cleanStyles}
      {...props}
    />
  );
});

Textarea.displayName = 'Textarea';

export default Textarea;
